/**
 * Offline storage: the write queue, and a cache of the last-known reads.
 *
 * SCOPING IS A SECURITY PROPERTY, NOT TIDINESS. The database NAME carries the
 * org, the user and the role — `dalan_outbox_{org}__{user}__{role}`. School
 * devices are shared: a phone at the desk is used by two cashiers, a tablet
 * passes between teachers. Without this, one person's queued marks would drain
 * under the next person's session, and one family's cached balance would be
 * readable by the next.
 *
 * GRACEFUL FALLBACK. IndexedDB is unavailable in private browsing and in some
 * locked-down WebViews. `openDb()` answers null there and every caller falls
 * back to online-only. Nothing in this module throws into the app: an offline
 * feature that breaks the online app is worse than no offline feature.
 */
import Dexie, { type Table } from "dexie";

/** One queued write. `seq` preserves insertion order — the drain replays in it. */
export type OutboxRow = {
  seq?: number;
  org_id: string;
  user_id: string;
  role: string;
  /** What kind of thing this is, so per-screen indicators can count their own. */
  entity: string;
  /** Org-relative: everything after `/orgs/{id}/`. Rebuilt at drain time from
   *  this row's own org_id, so a queued write cannot land in another school. */
  path: string;
  method: string;
  payload: unknown;
  /**
   * Generated ONCE at enqueue and reused on every replay. This is what makes
   * the queue safe: a row the server accepted but failed to acknowledge is
   * re-sent with the same key and deduped rather than applied twice.
   */
  idempotency_key: string;
  status: "pending" | "in_flight" | "failed";
  attempts: number;
  created_at: number;
  last_attempt_at: number;
  last_error?: string;
  /** Display-only, so the failed-row review can describe a row to a human
   *  without re-deriving anything from the payload. */
  meta?: Record<string, unknown> | null;
};

/** A cached GET, so a screen renders offline instead of an error. */
export type ReadRow = {
  path: string;
  payload: unknown;
  cached_at: number;
};

type OfflineDb = Dexie & {
  outbox: Table<OutboxRow, number>;
  reads: Table<ReadRow, string>;
};

let db: OfflineDb | null = null;
let scopeKey = "";
/** Latched once IndexedDB has proven unavailable, so we stop retrying it. */
let unavailable = false;

export type Scope = { orgId: string; userId: string; role: string };

let currentScope: Scope | null = null;

/** Called on sign-in. Changing scope closes the previous database. */
export function setOfflineScope(scope: Scope | null) {
  const key = scope ? `${scope.orgId}__${scope.userId}__${scope.role}` : "";
  if (key === scopeKey) return;
  db?.close();
  db = null;
  scopeKey = key;
  currentScope = scope;
}

export function offlineScope(): Scope | null {
  return currentScope;
}

/**
 * The database for the current scope, or null when there is no scope yet or
 * IndexedDB is unusable.
 */
export function openDb(): OfflineDb | null {
  if (unavailable || !scopeKey) return null;
  if (db) return db;
  try {
    const instance = new Dexie(`dalan_outbox_${scopeKey}`) as OfflineDb;
    instance.version(1).stores({
      // `++seq` auto-increments and preserves order. The compound index lets a
      // read filter by scope even though the DB name already isolates it —
      // belt and braces on data that includes money.
      outbox: "++seq, [org_id+user_id+role], status, entity",
      reads: "&path, cached_at",
    });
    db = instance;
    return db;
  } catch {
    unavailable = true;
    return null;
  }
}

/** Stamped onto every queued row. */
export function scopeFields(): Pick<OutboxRow, "org_id" | "user_id" | "role"> {
  return {
    org_id: currentScope?.orgId ?? "",
    user_id: currentScope?.userId ?? "",
    role: currentScope?.role ?? "",
  };
}

/** Test seam: forget the latched "unavailable" verdict between cases. */
export function resetForTest() {
  db?.close();
  db = null;
  scopeKey = "";
  currentScope = null;
  unavailable = false;
}
